/* eslint-disable no-console */
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { URLHelper } from 'src/helpers';
import { clone, cloneDeep, concat, filter, forIn, includes, isArray, isEmpty, isEqual, isObject, merge, transform, uniq } from 'lodash';
import * as ServiceAPI from 'src/models';
import { FrameworkActionCreator } from '.';
import { IGenericState, IModifiedObject, IDictionary } from './GenericState';
import { IStoreState } from '..';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { AntiAI } from 'src/helpers/AntiAIHelper/AntiAIHelper';
import { isAxiosError } from 'src/types';
// import SimpleCrypto from 'simple-crypto-js';

export const eraClient = axios.create({
    headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,POST,OPTIONS',
    },
});

export const CancelTokenDigiDataServiceCalls = axios.CancelToken.source();

// Used by multiple functions.
export interface ITreeDiveProps<T, U extends ITreeDiveProps<unknown, any>[]> {
    stateSlice: IGenericState<T>;
    diveProps?: U;
    options?: {
        controllerName?: string;
        skipServiceCallMerge?: boolean;
        setLoadingFlag?: boolean;
    };
}

export interface PickReturn<T> {
    APP: T;
    framework: T;
}

export interface PickRtn<T> {
    APP: T;
    status: string;
}

/**
 * This function determins which ids are not yet in the store and only calls the service to
 * fetch the ones missing.
 * @param Ids Primary Ids of the records needed.
 * @param state Slice of state being acted apon.
 * @param dispatch For dispatching actions.
 * @param options For more control where needed.
 */
export async function acGenericSelectByIds<T>(
    Ids: number[],
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    options: {
        force?: boolean,
        /** Defaults to stateSliceName */
        controllerName?: string,
        /** Defaults to SelectByIds */
        controllerMethod?: string,
        /** Do not set loading flag */
        noLoading?: boolean,
    } = {},
): Promise<ServiceAPI.SearchRtn<T>> {
    // Compare requested ids vs ids in the store.
    const idsToGet = options.force ? Ids : acGenericGetIdsNotInStore(Ids, state, dispatch);
    const searchRtn = new ServiceAPI.SearchRtn<T>();

    searchRtn.RecordList = Ids.filter((id) => idsToGet.includes(id)).map((id) => state.dataList[id]);

    if (idsToGet.length === 0) {
        // Return early if there are no new ids to get.
        return searchRtn;
    }

    // Let components know something is about to be loaded in case spinners or something
    // else needs to display while retrieving data.
    if (!options.noLoading) {
        dispatch({ type: state.stateSliceName + '/UpdatePending' });
    }

    // Service Input.
    const postData = new ServiceAPI.SearchInp();
    postData.IDList = Array.from(new Set(idsToGet));

    const response = await digiDataServiceCall<ServiceAPI.SearchRtn<T>>(
        postData,
        options.controllerMethod ?? 'SelectByIds',
        dispatch,
        options.controllerName ?? state.stateSliceName);

    // Push new data without blowing away records that were not returned on this call.
    acGenericDataListMergeWithSearchRtn(
        (response as AxiosResponse<ServiceAPI.SearchRtn<T>>).data, state, dispatch, { noLoading: options.noLoading });
    // dispatch({ type: stateSliceName + '/PendingIds_Remove', payload: idsToGet });

    if ('data' in response) {
        // combine the data in the store with the data that was fetched
        searchRtn.RecordList = [
            ...searchRtn.RecordList,
            ...response.data.RecordList,
        ];
    }

    // returns all the data
    return searchRtn;
}

/**
 * Compare IDs passed in to IDs in the store and return only the ones that are not yet in the store.
 * @param Ids
 * @param state
 * @param dispatch For dispatching actions.
 */
export function acGenericGetIdsNotInStore<T>(
    Ids: number[],
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): number[] {
    // Compare requested ids vs ids in the store.
    const existingIds = [
        ...Object.keys(state.dataList).map((k) => Number(k)),
        ...state.pendingIds,
    ];

    const idsToGet = Ids.filter((x) => x > 0 && existingIds.indexOf(x) < 0);

    if (idsToGet.length) {
        dispatch({ type: state.stateSliceName + '/PendingIds_Add', payload: idsToGet });
    }

    return idsToGet;
}

/**
 * This function determines which ids are not yet recorded by a 'SelectByOtherIds' call.
 * @param Ids The list of Ids to check for.
 * @param state The state being checked.
 * @param otherIdsName The non-primary key to read by (e.g. 'VehicleIds', 'ClientIds')
 */
export function acGenericGetOtherIdsNotInStore<T>(
    Ids: number[],
    state: IGenericState<T>,
    otherIdsName: string,
): number[] {
    const holdList = Ids.filter((x) => !includes(state.otherIds[otherIdsName], x));

    return uniq(holdList);
}

/**
 * This function records Ids that have been fetched to the 'otherIds' dictionary of the selected state.
 * @param Ids Ids to be recorded.
 * @param state The state to update the otherIds dictionary for.
 * @param dispatch Dispatch action.
 * @param otherIdsName The non-primary key to read by (e.g. 'VehicleIds', 'ClientIds')
 */
export function acGenericAddOtherIdsToStore<T>(
    Ids: number[],
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    otherIdsName: string,
) {
    const holdOtherIds = cloneDeep(state.otherIds);
    holdOtherIds[otherIdsName] = concat(Ids, state.otherIds[otherIdsName]);
    dispatch({ type: state.stateSliceName + '/Update_otherIds', payload: holdOtherIds });
}

/**
 * This function determines which ids are not yet in the store and only calls the service to
 * fetch the ones missing.  To use this method, the related controller in DigiDataService needs
 * to have a SelectByXXX method.  You set the XXX with the idsName field.  If this method is called
 * again to read by an id already read by, it won't call the service for that id.  One less thing you
 * have to worry about.
 * @param Ids Primary Ids of the records needed.
 * @param controllerIdsName SelectByXXX.  You set XXX.  Examples: ClientIds, VehicleIds, DMSBranchIds
 * @param state Slice of state being acted apon.
 * @param dispatch For dispatching actions.
 * @param otherIdName The ids your are trying to read by.
 */
export async function acGenericSelectByOtherIds<T>(
    Ids: number[],
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    otherIdName: string,
    options: {
        force?: boolean,
        /** Defaults to stateSliceName */
        controllerName?: string,
        /** Defaults to SelectByIds */
        controllerMethod?: string,
        /** Do not set loading flag */
        noLoading?: boolean,
    } = {},
): Promise<ServiceAPI.SearchRtn<T>> {
    // Compare requested ids vs ids in the store.
    const idsToGet = (options.force) ? Ids : acGenericGetOtherIdsNotInStore(Ids, state, otherIdName);
    // If there are no new ids, return nothing.
    if (idsToGet.length === 0) {
        return new ServiceAPI.SearchRtn();
    }

    // Push which ids we are pulling by, so we won't pull again.
    acGenericAddOtherIdsToStore(idsToGet, state, dispatch, otherIdName);

    // Let components know something is about to be loaded in case spinners or something
    // else needs to display while retrieving data.
    if (!options.noLoading) {
        dispatch({ type: state.stateSliceName + '/UpdatePending' });
    }

    // Service Input.
    const postData = new ServiceAPI.SearchInp();
    postData.IDList = Array.from(new Set(idsToGet));

    const response = await digiDataServiceCall(
        postData,
        options.controllerMethod || 'SelectBy' + otherIdName,
        dispatch,
        options.controllerName || state.stateSliceName);

    // Push new data without blowing away records that were not returned on this call.
    acGenericDataListMergeWithSearchRtn(
        (response as AxiosResponse<ServiceAPI.SearchRtn<T>>).data, state, dispatch, { noLoading: options.noLoading });

    return (response as AxiosResponse<ServiceAPI.SearchRtn<T>>).data;
}

/**
 * Creates a new entry into the modifiedDataList.
 * @param emptyObject This should be the default object for the class.
 * @param state Slice of store being modified.
 * @param dispatch for dispatching reducer actions.
 */
export function acGenericAddObject<T>(
    initialObject: Readonly<T>,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): number {
    const modifiedObject: IModifiedObject<T> = {
        modifiedObject: initialObject,
        originalObject: { ...initialObject },
        addFlag: true,
        isModified: false,
        deleteFlag: false,
        createdTimestamp: new Date(),
    };

    // An id is needed so this object can be pulled again if they don't commit the add right away.
    // The add flag will indicate this id is not an actual record id in the database.
    modifiedObject.modifiedObject[state.idName] = new Date().getTime();

    const newState = { ...state.modifiedDataList };
    newState[modifiedObject.modifiedObject[state.idName]] = modifiedObject;
    dispatch({ type: state.stateSliceName + '/Update_modifiedDataList', payload: newState });

    return modifiedObject.modifiedObject[state.idName];
}

/**
 * Modify a single value in a record object.
 * @param fieldName Name of the field being modified.
 * @param value New Value.
 * @param state Slice of store being modified.
 * @param dispatch for dispatching reducer actions.
 * @param id Primary id of the object being modified.
 */
export function acGenericModifiedField<T>(
    id: number,
    fieldName: string | string[],
    value: T[keyof T],
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): void {
    const modifiedObject = state.modifiedDataList[id];

    const holdObject =
        modifiedObject !== null ?
            isArray(fieldName) ?
                cloneDeep(modifiedObject.modifiedObject) :
                clone(modifiedObject.modifiedObject) :
            isArray(fieldName) ?
                cloneDeep(state.dataList[id]) :
                clone(state.dataList[id]);

    // Update object and push.
    if (isArray(fieldName)) {
        let schema = holdObject;
        const len = fieldName.length;

        for (let index = 0; index < len - 1; ++index) {
            const elem = fieldName[index];

            if (!schema[elem]) {
                schema[elem] = {};
            }

            schema = schema[elem];
        }

        schema[fieldName[len - 1]] = value;
    } else {
        holdObject[fieldName] = value;
    }

    acGenericModifiedObject(holdObject, state, dispatch);
}

/**
 * Pushes modified data to the store.
 * @param inpObject The modified object.
 * @param state slice of state being acted apon.
 * @param dispatch for dispatching actions.
 * @param idName Name of the primary id field.
 * @param stateSliceName Name of the class of the input object.
 */
export function acGenericModifiedObject<T>(
    inpObject: T,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): void {
    const id = inpObject[state.idName];

    const newLoad = { ...state.modifiedDataList };

    if (state.modifiedDataList[id]) {
        // Keep original object if the object was already modified.  Remove old item from list.
        newLoad[id] = { ...state.modifiedDataList[id], modifiedObject: inpObject };
        newLoad[id].isModified = true;
    } else {
        // Pull current object from datalist.  An add should never ever hit this list.  If you are here
        // on an add, you need to call acGenericAddObject first.
        newLoad[id] = {
            modifiedObject: inpObject,
            originalObject: state.dataList[id],
            addFlag: false,
            isModified: true,
            deleteFlag: false,
            createdTimestamp: new Date(),
        };
    }

    dispatch({ type: state.stateSliceName + '/Update_modifiedDataList', payload: newLoad });
}

/**
 * Delete an item from the redux store and the server's database.
 * @param id Primary id of object to delete.
 * @param state Slice of state the object resides in.
 * @param dispatch for dispatching actions.
 * @param skipDBUpdate Delete locally only.
 */
export function acGenericDelete<T>(
    id: number,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    options: {
        skipDBUpdate?: boolean,
        skipAbort?: boolean,
    } = {},
): void {
    if (id <= 0) {
        return;
    }

    const deleteObject = {};
    deleteObject[state.idName] = id;

    if (state.dataList[id]) {
        const DMSStoreId = 'DMSStoreId';
        // DMSStoreId is needed for alot of deletes.
        if (!!state.dataList[id][DMSStoreId]) {
            deleteObject[DMSStoreId] = state.dataList[id][DMSStoreId];
        }
        // DMSBranchId is needed for alot of deletes.
        const DMSBranchId = 'DMSBranchId';
        // Remove entry from datalist.
        if (!!state.dataList[id][DMSBranchId]) {
            deleteObject[DMSBranchId] = state.dataList[id][DMSBranchId];
        }
        // Remove entry from datalist.
        const newLoad = { ...state.dataList };
        delete newLoad[id];
        dispatch({ type: state.stateSliceName + '/Update_dataListDelete', payload: newLoad });
    }

    // Delete uncomitted changes too.
    if (!options.skipAbort) {
        acGenericAbortChanges(
            deleteObject[state.idName],
            state,
            dispatch,
        );
    }

    // Send the delete to the service.
    if (options.skipDBUpdate) {
        return;
    }
    digiDataServiceCall(deleteObject, 'Delete', dispatch, state.stateSliceName);
}

/**
 * Flush out a State in the Store
 * @param state Slice of state the object resides in.
 * @param dispatch for dispatching actions.
 */
export function acGenericFlushState<T>(
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): void {
    dispatch({ type: state.stateSliceName + '/Update_dataListFlush', payload: {} });
}

/**
 * Set a modified item to logically deleted.
 * @param id Primary id of object to delete.
 * @param state Slice of state the object resides in.
 * @param dispatch for dispatching actions.
 */
export function acGenericLogicallyDelete<T>(
    id: number,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): void {
    if (id <= 0) {
        return;
    }

    // If the object doesn't exist in the actual store, just abort changes.
    if (!state.dataList[id]) {
        acGenericAbortChanges(id, state, dispatch);
    } else {
        let modifiedObject = state.modifiedDataList[id];
        const holdObject =
            modifiedObject !== null ?
                clone(modifiedObject.modifiedObject) :
                clone(state.dataList[id]);

        const newLoad = { ...state.modifiedDataList };

        if (state.modifiedDataList[id]) {
            // Keep original object if the object was already modified.  Remove old item from list.
            modifiedObject = { ...state.modifiedDataList[id], modifiedObject: holdObject };
            modifiedObject.isModified = true;
            modifiedObject.deleteFlag = true;
            delete newLoad[id];
        } else {
            // Pull current object from datalist.  An add should never ever hit this list.  If you are here
            // on an add, you need to call acGenericAddObject first.
            modifiedObject = {
                modifiedObject: holdObject,
                originalObject: state.dataList[id],
                addFlag: false,
                isModified: true,
                deleteFlag: true,
                createdTimestamp: new Date(),
            };
        }
        newLoad[id] = modifiedObject;

        dispatch({ type: state.stateSliceName + '/Update_modifiedDataList', payload: newLoad });
    }
}

/**
 * Deletes uncommitted changes.  In more technical terms, deletes modifications in the redux store before
 * they are committed to the server.
 * @param id The primaryId of the object to abort changes for.
 * @param state slice of state the object belongs to.
 * @param dispatch for dispatching actions.
 */
export function acGenericAbortChanges<T>(
    id: number,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
): void {
    if (state.modifiedDataList[id]) {
        // Remove entry from datalist.
        dispatch({ type: state.stateSliceName + '/AbortChanges_modifiedDataList', payload: id });
    }
}

interface IAbortTreeProps<T, U extends ITreeDiveProps<unknown, any>[]> {
    relatedId: number;
    relatedIdName: string;
    state: IGenericState<T>;
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    diveProps?: U;
}

/**
 * Deletes all records related to a base record.
 * At the top level, the idName and relatedIdName is the same thing.
 */
export function acGenericAbortTree<T, U extends ITreeDiveProps<unknown, any>[]>(props: IAbortTreeProps<T, U>): void {
    const diveIds: number[] =
        filter(props.state.modifiedDataList, (x) => x.modifiedObject[props.relatedIdName] === props.relatedId)
            .map((x) => x.modifiedObject[props.state.idName]);

    for (const id of diveIds) {
        acGenericAbortChanges(
            id,
            props.state,
            props.dispatch,
        );
    }

    if (props.diveProps) {
        filter(props.state.dataList, (x) => x[props.relatedIdName] === props.relatedId)
            .forEach((x) => diveIds.push(x[props.state.idName]));

        uniq(diveIds).forEach((x) => {
            if (props.diveProps) {
                props.diveProps.forEach((y) => {
                    acGenericAbortTree({
                        relatedId: x,
                        relatedIdName: props.state.idName,
                        state: y.stateSlice,
                        dispatch: props.dispatch,
                        diveProps: y.diveProps,
                    });
                });
            }
        });
    }
}

/**
 * Commit changes from an item in the modifiedDataList to the service.
 * @param inpObject
 * @param state
 * @param dispatch
 */
export async function acGenericCommit<T>(
    id: number,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    options: {
        controllerName?: string,
        skipServiceCallMerge?: boolean,
        insertMethod?: string,
        updateMethod?: string,
        postCleanup?: boolean,
        setLoadingFlag?: boolean,
    } = {},
): Promise<void> {
    const modifiedObject = state.modifiedDataList[id];

    if (modifiedObject === undefined) {
        return;
    }

    const controllerName = options.controllerName ?? state.stateSliceName;
    const insertMethod = options.insertMethod ?? 'Insert';
    const updateMethod = options.updateMethod ?? 'Update';
    const postCleanup = options.postCleanup ?? false;
    const setLoadingFlag = options.setLoadingFlag ?? false;
    const skipServiceCallMerge = options.skipServiceCallMerge ?? false;

    const skipCleanup = modifiedObject.deleteFlag;

    let responsePromise: Promise<AxiosResponse<ServiceAPI.SearchRtn<T>> | AxiosError<ServiceAPI.SearchRtn<T>>> | undefined =
        undefined;

    if (modifiedObject.isModified) {
        if (modifiedObject.addFlag) {
            const postData = { ...modifiedObject?modifiedObject : ''};
            postData[state.idName] = -1;
            responsePromise = digiDataServiceCall(postData, insertMethod, dispatch, controllerName);
        } else if (modifiedObject.deleteFlag) {
            acGenericDelete(id, state, dispatch);
        } else {
            const postData = new ServiceAPI.UpdateInp<T>();
            postData.OriginalRecord = modifiedObject.originalObject;
            postData.UpdatedRecord = modifiedObject.modifiedObject;
            // If the entry is in the datalist, update it now without waiting for the service
            // to return.  Also, only update whats changed as the datalist may have gotten updates
            // after the modifications actually started.
            if (state.commitCallback) {
                const fields: string[] = [];
                forIn(postData.UpdatedRecord, (objValue, objKey) => {
                    if (!!postData.OriginalRecord && !isEqual(objValue, postData.OriginalRecord[objKey])) {
                        fields.push(objKey);
                    }
                });
                state.commitCallback(postData.UpdatedRecord, fields);
            }
            if (state.dataList[id] !== undefined) {
                const updatedDataListEntry = cloneDeep(state.dataList[id]);
                forIn(postData.UpdatedRecord, (objValue, objKey) => {
                    if (!!postData.OriginalRecord && !isEqual(objValue, postData.OriginalRecord[objKey])) {
                        // const propDesciptor = Object.getOwnPropertyDescriptor(updatedDataListEntry, objKey);
                        // if (propDesciptor && propDesciptor.writable) {
                        updatedDataListEntry[objKey] = objValue;
                        // }
                    }
                });
                acGenericDataListLoad([updatedDataListEntry], state, dispatch, {
                    noLoading: true,
                });
            }
            responsePromise = digiDataServiceCall(postData, updateMethod, dispatch, controllerName);
        }
    }

    if (!responsePromise) {
        return;
    }

    if (!skipCleanup) {
        // Process the response from the server and then clean up the modified list.
        if (!postCleanup) {
            acGenericAbortChanges(id, state, dispatch);
        }

        // Let components know something is about to be loaded in case spinners or something
        // else needs to display while retrieving data.
        if (setLoadingFlag) {
            dispatch({ type: state.stateSliceName + '/UpdatePending' });
        }

        const response = await responsePromise;
        const rtnData = (response as AxiosResponse<ServiceAPI.SearchRtn<T>>).data;

        if (postCleanup) {
            acGenericAbortChanges(id, state, dispatch);
        }

        if (!skipServiceCallMerge) {
            acGenericDataListMergeWithSearchRtn(rtnData, state, dispatch,
                { skipModifiedMerge: true, skipToBeDeletedEntries: true });
        }
    } else {
        await responsePromise;
    }
}


export function acLocalUpdate<T>(
    modifiedObject: Readonly<T>,
    fields: string[],
    id: string,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    state: IGenericState<T>,
): void {
    const objects = Object.values(state.dataList).filter((x) => x[id] === modifiedObject[id]);

    if (objects.length) {
        for (const field of fields) {
            const desc = Object.getOwnPropertyDescriptor(objects[0], field);

            if (desc?.value !== undefined && (desc.set || desc.writable)) {
                for (const x of objects) {
                    // TODO(harrisj1): Assignment of readonly field???
                    x[field] = modifiedObject[field];
                }
            }
        }

        const newLoad = { ...state.dataList };

        for (const obj of objects) {
            newLoad[obj[state.idName]] = obj;
        }

        const payload = { dataList: newLoad, noLoading: true };

        dispatch({ type: state.stateSliceName + '/Update_dataList', payload: payload });
    }

    const modified = Object.values(state.modifiedDataList).filter((x) => x.modifiedObject[id] === modifiedObject[id]);

    if (modified.length) {
        for (const field of fields) {
            const desc = Object.getOwnPropertyDescriptor(modified[0].modifiedObject, field);

            if (desc?.value !== undefined && (desc.set || desc.writable)) {
                for (const x of modified) {
                    x.modifiedObject[field] = modifiedObject[field];
                }
            }
        }

        const modLoad = { ...state.modifiedDataList };

        for (const mod of modified) {
            modLoad[mod.modifiedObject[state.idName]] = mod;
        }

        dispatch({ type: state.stateSliceName + '/Update_modifiedDataList', payload: modLoad });
    }
}

interface ICommitTreeProps<T, U extends ITreeDiveProps<unknown, any>[]> {
    stateSlice: IGenericState<T>;
    relatedIdName: string;
    relatedId: number;
    dispatch: ThunkDispatch<IStoreState, never, Action>;
    diveProps?: U;
    options?: {
        controllerName?: string;
        skipServiceCallMerge?: boolean;
    };
}

/**
 * Abstracts the process of committing a tree of records.  It will take the key used to update the previous level to find
 * records to commit on the next dive.  Saves on code needed.  It will probably be beneficial to go and find an example of use.
 * At the top level, the idName and relatedIdName is the same thing.
 */
export async function acGenericCommitTree<T, U extends ITreeDiveProps<unknown, any>[]>(
    props: ICommitTreeProps<T, U>,
): Promise<void> {
    // If needed.
    if (!props.options) {
        props.options = {};
    }

    const diveIds =
        Object.values(props.stateSlice.modifiedDataList)
            .filter((x) => x.modifiedObject[props.relatedIdName] === props.relatedId)
            .map((x) => x.modifiedObject[props.stateSlice.idName]);

    // Commit.
    const promiseList = diveIds.map((id) => (
        acGenericCommit(
            id,
            props.stateSlice,
            props.dispatch,
            { ...props.options },
        )
    ));

    // Dive if needed.
    if (props.diveProps) {
        Object.values(props.stateSlice.dataList)
            .filter((x) => x[props.relatedIdName] === props.relatedId)
            .forEach((x) => diveIds.push(x[props.stateSlice.idName]));

        uniq(diveIds).forEach((x) => {
            if (props.diveProps) {
                props.diveProps.forEach((y) => {
                    promiseList.push(acGenericCommitTree({
                        stateSlice: y.stateSlice,
                        dispatch: props.dispatch,
                        relatedIdName: props.stateSlice.idName,
                        relatedId: x,
                        options: y.options,
                        diveProps: y.diveProps,
                    }));
                });
            }
        });
    }

    // Lets the caller wait till the update is complete if needed.
    await Promise.all(promiseList);
}

/**
 * Merges the searchrtn data with the data list.  This method allows the caller to not
 * know what searchRtn is.  The caller can just push their response data to this
 * method.
 * @param id
 * @param state
 * @param dispatch
 */
export function acGenericDataListMergeWithSearchRtn<T>(
    searchRtn: ServiceAPI.SearchRtn<T> | undefined,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    options: IGenericDataListLoadOptions = {},
): void {
    /** Checks in case the response object in .then((response) => {}) is empty.  */
    if (searchRtn) {
        acGenericDataListLoad(searchRtn.RecordList, state, dispatch, options);
    }
}

interface IGenericDataListLoadOptions {
    noLoading?: boolean;
    skipModifiedMerge?: boolean;
    skipToBeDeletedEntries?: boolean;
}
/**
 * Merges the inpList with the state's dataList without blowing away data not
 * included in the inpList.  If its a new primary key id, then its added to the
 * data list.  If its an existing id in the data list, then the data list is updated.
 * If the inpList does not include an id thats in the datalist, that item in the
 * data list is untouched.
 * @param id
 * @param state
 * @param dispatch
 */
export function acGenericDataListLoad<T>(
    inpList: T[] | undefined,
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    options: IGenericDataListLoadOptions = {},
): void {
    if (!inpList) {
        // In case any loading flags need to be cleared.
        dispatch({
            type: state.stateSliceName + '/Update_pendingLoadCountDecrement',
            payload: { noLoading: options.noLoading },
        });

        return;
    }

    // We need to sync the modified data list.
    if (!options.skipModifiedMerge && !isEmpty(state.modifiedDataList)) {
        let updateModifiedList = false;
        const modifiedList = { ...state.modifiedDataList };

        inpList.forEach((x) => {
            const id = x[state.idName];

            if (modifiedList[id]) {
                const diffObject = difference(x, modifiedList[id].originalObject);

                if (!isEmpty(diffObject)) {
                    const originalObject = merge({}, { ...modifiedList[id].originalObject! }, diffObject);
                    const modifiedObject = merge({}, { ...modifiedList[id].modifiedObject }, diffObject);
                    const newObject: IModifiedObject<T> = {
                        ...modifiedList[id],
                        originalObject: originalObject,
                        modifiedObject: modifiedObject,
                    };

                    modifiedList[id] = newObject;
                    updateModifiedList = true;
                }
            }
        });

        // We found a modified record, now we need to update the state.
        if (updateModifiedList) {
            dispatch({ type: state.stateSliceName + '/Update_modifiedDataList', payload: modifiedList });
        }
    }

    // Update the data list.
    const newLoad = { ...state.dataList };

    for (const x of inpList) {
        newLoad[x[state.idName]] = x;
    }

    const payload = { dataList: newLoad, noLoading: options.noLoading };

    if (options.skipToBeDeletedEntries && !isEmpty(state.modifiedDataList)) {
        const modifiedList = Object.values(state.modifiedDataList);

        for (const x of modifiedList) {
            if (x.deleteFlag) {
                // remove to be deleted entries from new data
                delete newLoad[x.modifiedObject[state.idName]];
            }
        }
    }

    // Push new data without blowing away records that were not returned on this call.
    dispatch({ type: state.stateSliceName + '/Update_dataList', payload: payload });
}

/**
 * Merges the inpList with the state's dataList without blowing away data not
 * included in the inpList.  If its a new primary key id, then its added to the
 * data list.  If its an existing id in the data list, then the data list is updated.
 * If the inpList does not include an id thats in the datalist, that item in the
 * data list is untouched.
 * @param id
 * @param state
 * @param dispatch
 */
export function acGenericDataSearchLoad<T>(
    inpList: T[],
    state: IGenericState<T>,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    totalCount: number,
): void {
    const newLoad = { ...state.dataList };

    for (const x of inpList) {
        newLoad[x[state.idName]] = x;
    }

    const payload = { dataList: newLoad, noLoading: false };

    // Push new data without blowing away records that were not returned on this call.
    dispatch({ type: state.stateSliceName + '/SetCount', payload: totalCount });
    dispatch({
        type: state.stateSliceName + '/Update_dataList',
        payload: payload,
    });
}

/**
 * This function pushes updates to the state's dataList. This should only be used for actions
 * that perform updates without using the modified state.
 * @param updateObject
 * @param state
 * @param dispatch
 */
export function acGenericDataListPushUpdate<T>(
    updateObject: T,
    state: IGenericState<T>,
    dispatch: (action: { type: string, payload?: IDictionary<unknown> }) => void,
    options?: { addNew?: boolean },
): void {
    if (!updateObject) {
        return;
    }

    let index = updateObject[state.idName];
    const addIfNotThere = options?.addNew ?? false;

    if (!state.dataList[index]) {
        if (!addIfNotThere) {
            return;
        }

        index = new Date().getTime();
    }

    const newLoad = { ...state.dataList };
    const diffObject = difference(updateObject, newLoad[index]);
    newLoad[index] = merge({}, newLoad[index], diffObject);
    const payload = { dataList: newLoad, noLoading: true };

    // Push new data without blowing away records that were not returned on this call.
    dispatch({ type: state.stateSliceName + '/Update_dataList', payload: payload });
}

export async function eraWebServiceCall<T>(
    program: string,
    command: string,
    progData: unknown,
    storeId?: string,
    branchId?: string,
    runArea?: string,
): Promise<T | AxiosError<{ stdout: { rspData: string } }> | AxiosError<{ data: string }> | any> {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    function handleNetworkChange() {
        if (!navigator.onLine) {
            source.cancel();
        }
    }
    window.addEventListener('offline', handleNetworkChange);

    try {
        const varAntiAI = await AntiAI(command);

        const store = !storeId ? '01' : storeId;
        const branch = !branchId ? '01' : branchId;
        const app = !runArea ? 'servc' : runArea;

        const reqData = {
            'PWR': { 'program': program, 'store': store, 'app': app, 'branch': branch },
            'APP': {
                command: command,
                data: progData,
                key: varAntiAI,
            },
        };

        // We want to send encrypted requests for Production systems to prevent data from being seen in Dev Tools (PII)
        // const type = window.location.href.slice(8, 10).toUpperCase();
        // Send normal JSON in QA or Dev
        // if (type === 'RD' || type === 'RI' || sysNo === 'lhost') {
        const url = URLHelper.getDataServiceUrl();
        const resp: { data: { PWR: string, APP: string, error?: string }, status: string } =
            await eraClient.post(url, reqData, {cancelToken: source.token});
        const { APP } = resp.data;

        window.removeEventListener('offline', handleNetworkChange);

        return APP;
        // }
        // Encrypt the transaction to the API.  Decrypt the response when received
        // const simpleCrypto = new SimpleCrypto(process.env.API_TOKEN);
        // const url = URLHelper.getDataServiceUrl('/prod');
        // const ciphertext = simpleCrypto.encrypt(reqData);
        // const resp: { data: string, status: string } = await axios.post(url, { data: ciphertext }, config);
        // const decryptedRes = simpleCrypto.decrypt(resp.data);

        // return decryptedRes['APP'];
    } catch (err) {
        // Comment out for now until Brian decides how we want to handle access to the state outside of a React component.
        // switch (err.code) {
        //     case '500':
        //     case '404':
        //     case '504':
        //         SetERAError(err.code);
        //         break;
        //     default:
        //         break;
        // }

        window.removeEventListener('offline', handleNetworkChange);

        // If unauthorized logout.
        return err as AxiosError<{ stdout: { rspData: string } }>;
    }
}

/**
 * Abstracts calls to the DigiDataService.
 * @param postData
 * @param urlTarget
 * @param dispatch
 * @param controllerName
 */

export async function rtsWebServiceCall<T>(
    postData: unknown,
    urlTarget: string,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    controllerName: string,
    token?: string,
): Promise<AxiosResponse<T> | unknown> {
    try {
        // /websession/UVMWEBAP
        const url = URLHelper.getDataServiceUrl('web-2-exe');
        if (!!token) {
            const config: AxiosRequestConfig = {
                cancelToken: CancelTokenDigiDataServiceCalls.token,
                headers: {
                    'content-type': 'text/plain',
                    'websession-token': token,
                },
            };

            return await axios.post(url, postData, config);
        } else {
            const config: AxiosRequestConfig = {
                cancelToken: CancelTokenDigiDataServiceCalls.token,
            };

            return await axios.post(url, postData, config);
        }
    } catch (err) {
        // If unauthorized logout.
        // TODO(harrisj1): Is this going to be of the expected return type AxiosResponse<T>?
        return err;
    }
}

/**
 * Abstracts calls to the DigiDataService.
 * @param postData
 * @param urlTarget
 * @param dispatch
 * @param controllerName
 */
export async function digiDataServiceCall<T>(
    postData: {} | null,
    urlTarget: string,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    controllerName: string,
): Promise<AxiosResponse<T> | AxiosError<T>> {
    try {
        const url = URLHelper.getDataServiceUrl(controllerName + '/' + urlTarget);
        const response = await axios.post(url, postData, { cancelToken: CancelTokenDigiDataServiceCalls.token });

        return response;
    } catch (err) {
        const axiosError = err as AxiosError<T>;
        // If unauthorized logout.
        if (axiosError.request && axiosError.request.status && axiosError.request.status.toString() === '401') {
            dispatch(FrameworkActionCreator.logout());
        } else {
            // Otherwise, log error to the slice of store.
            dispatch({ type: controllerName + '/ERROR', payload: axiosErrorMessage(axiosError) });
        }

        return axiosError;
    }
}

/**
 * Centralized handler for axios failures returned from eraWebServiceCall.
 *
 * Returns true if response is an AxiosError. On error:
 *  - If options.fallbackHandler is provided, runs it (caller-managed substitution).
 *  - Otherwise, dispatches Framework/ERA_ERROR to trigger the global Oops overlay (see App.tsx).
 * Returns a type predicate so callers can write `if (handleAxiosError(...)) return;`
 * and have the response narrowed to the success type on the happy path.
 */
export function handleAxiosError<T>(
    response: T | AxiosError,
    dispatch: ThunkDispatch<IStoreState, never, Action>,
    options?: { fallbackHandler?: () => void; operation?: string },
): response is AxiosError {
    if (!response || !isAxiosError(response)) {
        return false;
    }
    if (options?.fallbackHandler) {
        options.fallbackHandler();

        return true;
    }
    dispatch({
        type: 'Framework/ERA_ERROR',
        payload: axiosErrorMessage(response) || (options?.operation ?? 'Unknown axios error'),
    });

    return true;
}

/**
 * Builds an error message from an AxiosError.
 * @param err
 */
export function axiosErrorMessage(err: AxiosError): string {
    let rtnErrorMsg: string = '';

    // I did not want to type this three times.
    const appendErrFieldToMessage = (fieldName: string): void => {
        if (err.request[fieldName]) {
            rtnErrorMsg += err.request[fieldName] + ' ';
        }
    };

    // Try to build the error from the response from the service if possible.
    // Otherwise use the AxiosError.message field.
    if (err.request) {
        appendErrFieldToMessage('status');
        appendErrFieldToMessage('statusText');
        appendErrFieldToMessage('responseText');
    }
    if (rtnErrorMsg === '') {
        rtnErrorMsg = err.message;
    }

    return rtnErrorMsg.trim();
}

/**
 * Deep diff between two object, using lodash
 * @param  compare - Object compared
 * @param  original - Object to compare with
 * @return Return a new object who represent the diff
 */
export function difference<T extends {}>(compare: T, original: T | undefined): {} {
    if (!original) {
        return compare;
    }

    return transform(compare, (result, value, key) => {
        if (original[key] && !isEqual(value, original[key])) {
            result[key] = isObject(value) && isObject(original[key]) ? difference(value, original[key]) : value;
        }
    }, {});
}
// branch can contain more than one branc in the input.  It should look like
// '"SERVC01", "ACCTG01"'
// docType can contain more than one document type in the input.  It should look like
// '"VCR-OSD", "SVCRPTCARD-OSD"'
export async function eraIDMQueyr<T>(
    docToken: string,
    store: string,
    branch: string,
    docType: string,
    key1: string,
    key1value: string,
    key2: string,
    key2value: string,
): Promise<T | AxiosError<{ stdout: { rspData: string } }> | AxiosError<{ data: string }> | any> {
    try {
        let type = window.location.href.slice(8, 10).toUpperCase();
        let sysNo = window.location.href.slice(10, 16);
        let url = 'https://' + type + sysNo + '.cdn.reyrey.net/api/graphql';
        if (sysNo === 'alhost') {
            type = 'ri';
            sysNo = '080027';
            url = '/api/graphql';
        }
        //  The Key below is hard coded and only works for 80027.  This is NOT how this should work once live
        // eslint-disable-next-line max-len
        // const token = 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6ImludGVybmFsIn0.eyJwcmVmZXJyZWRfdXNlcm5hbWUiOiJ3ZWJhcGl0ZXN0IiwicXVlcnlhY2wiOltdLCJpYXQiOjE2NTc2NDczNDYsImV4cCI6MTY2NTQyMzM0NiwiYXVkIjoiaTA4MDAyNy5jZG4ucmV5cmV5Lm5ldCIsImlzcyI6ImZvY3VzLmRlYWxlci5yZXlyZXkubmV0Iiwic3ViIjoidGVzdCJ9.MjtfefjPo59Fh7CZylQoO18r_5EhqELoMVATxLwlbKyNlyKTL_M0onoDvlM2h9VCYSi7XGT_evek0SFa-Bd3gENzxqIOgJ3dfrTtL8epPnU8HU8FDanW2T_F-iuYoQYoi3vsT0yQ_DNSBGl3Z4x_m4Y37hEf0Supwtlh-ug7pcGRU495qrTb8YsYNmUXR9OU8jfnW0Bzsks9VaUjDxFuapiLdrpAk9N-4am-Hz0iRJkES-Sbhx7T9CpNyMqBUfJ-XC2ISjvUNglSm_ANCV6aWxYMj6LvdBBg2woXOztkcB8D8P25t8z_lxFBe8yKOtZwlZRgQT6CZ7VgqZPn3kp58Q';
        const token = docToken;
        const config: AxiosRequestConfig = {
            headers: {
                'content-type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
        };
        const postData = {
            query: `query MyQuery($aFilters: [QueryFilter]) {
                    queryDocument(aFilters: $aFilters) {
                        doc_date
                        doc_uuid
                        doc_store
                        doc_type
                        doc_updated
                        branch
                    elements {
                        element_uuid
                        element_type
                        element_desc
                    }
                    keys {
                        name
                        value
                    }
                }
                }`,
            variables: `{"aFilters": [
        { "column": "doc_store", "operation": "QUERYEQUALS", "values": "` + store + `" },
        { "column": "branch", "operation": "QUERYIN", "values": [` + branch + `] },
        { "column": "doc_type", "operation": "QUERYIN", "values": [` + docType + `]},
        { "column": "` + key1 + `", "operation": "QUERYEQUALS", "values": "` + key1value + `"},
        { "column": "` + key2 + `", "operation": "QUERYEQUALS", "values": "` + key2value + `"}
        ]
    }`,
        };
        // { "column": "doc_type", "operation": "QUERYIN", "values": "` + docType + `" }
        // { "column": "keys", "operation": "QUERYEQUALS", "values": "` + '{"name":"cust","value":"109877"}' + `" }

        const response = await axios.post(url, postData, config);
        const resp = response.data;

        return resp;
    } catch (err) {
        // If unauthorized logout.
        // TODO(harrisj1): Is this going to be of the expected return type AxiosResponse<T>?
        return err;
    }
}

export async function getIDMDoc<T>(
    docToken: string,
    docUUID: string,
    elementUUID: string,
): Promise<T | AxiosError<{ stdout: { rspData: string } }> | AxiosError<{ data: string }> | any> {
    try {
        let type = window.location.href.slice(8, 10).toUpperCase();
        let sysNo = window.location.href.slice(10, 16);
        if (sysNo === 'alhost') {
            type = 'ri';
            sysNo = '080027';
        }
        const url = 'https://' + type + sysNo + '.cdn.reyrey.net/api/downloadElement/' + docUUID + '/' + elementUUID;
        const token = docToken;
        const config: AxiosRequestConfig = {
            headers: {
                Content: 'application/pdf',
                Authorization: 'Bearer ' + token,
            },
            responseType: 'blob',
        };

        const response = await axios.get(url, config);
        // const test = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        const returnURL = URL.createObjectURL(response.data);
        // window.open(URL.createObjectURL(response.data));
        // const resp = response.data;

        return returnURL;
    } catch (err) {
        // If unauthorized logout.
        // TODO(harrisj1): Is this going to be of the expected return type AxiosResponse<T>?
        return err;
    }
}